import { cartConstants } from '../actions/constants'

const CART_SCOPE_GLOBAL = 'global'
const CART_SCOPE_SESSION = 'session'

const SESSION_CART_MUTATION_REQUEST = 'SESSION_CART_MUTATION_REQUEST'
const SESSION_CART_MUTATION_SUCCESS = 'SESSION_CART_MUTATION_SUCCESS'
const SESSION_CART_MUTATION_FAILURE = 'SESSION_CART_MUTATION_FAILURE'

const initState = {
    cartItems: [],
    mainCartItems: [],
    sessionCartItems: [],
    activeCartScope: CART_SCOPE_GLOBAL,
    sessionCartContext: null,
    mainCartReady: false,
    sessionCartReady: false,
    sessionCartUpdatedAt: 0,
    updatingCart: false,
    updatingCartItems: {},
    showModal: false,
    error: null,
    textAlert: '',
    variantAlert: 'success',
    showAlert: false,
    loading: false,
}

const normalizeItems = (items) => {
    if (!items) return []
    if (Array.isArray(items)) return items.filter(Boolean)
    if (typeof items === 'object') return Object.values(items).filter(Boolean)
    return []
}

const getActionCartItemKey = (action) => {
    return String(action.payload?.cartItemKey || action.payload?.cartItemId || action.payload?.productId || '')
}

const setUpdatingCartItem = (updatingCartItems, cartItemKey, value) => {
    const nextUpdatingCartItems = {
        ...(updatingCartItems || {})
    }

    if (!cartItemKey) {
        return nextUpdatingCartItems
    }

    if (value) {
        nextUpdatingCartItems[cartItemKey] = true
    } else {
        delete nextUpdatingCartItems[cartItemKey]
    }

    return nextUpdatingCartItems
}

const getVisibleCartItems = (state, scope = state.activeCartScope) => {
    if (scope === CART_SCOPE_SESSION && state.sessionCartContext?.sessionId) {
        return normalizeItems(state.sessionCartItems)
    }

    return normalizeItems(state.mainCartItems)
}

const getSessionItemsFromPayload = (payload = {}) => {
    if (Array.isArray(payload.cartItems)) return payload.cartItems
    if (Array.isArray(payload.items)) return payload.items
    if (Array.isArray(payload.sessionCartItems)) return payload.sessionCartItems

    const session = payload.session || payload.activeSession || null

    if (Array.isArray(session?.orderDraftState?.products)) return session.orderDraftState.products
    if (Array.isArray(session?.calc3dState?.cartItems)) return session.calc3dState.cartItems
    if (Array.isArray(session?.catalogState?.selectionItems)) return session.catalogState.selectionItems

    return []
}

const getSessionContextFromPayload = (payload = {}, prevContext = null) => {
    const session = payload.session || payload.activeSession || null
    const officeId = String(
        payload.officeId ||
        session?.officeId?._id ||
        session?.officeId ||
        prevContext?.officeId ||
        ''
    ).trim()
    const sessionId = String(
        payload.sessionId ||
        session?.sessionId ||
        prevContext?.sessionId ||
        ''
    ).trim().toUpperCase()

    if (!officeId || !sessionId) return prevContext

    return {
        officeId,
        sessionId,
    }
}

const getSessionUpdatedAt = (payload = {}) => {
    const session = payload.session || payload.activeSession || null
    const raw = payload.updatedAt || session?.updatedAt || null
    const timestamp = raw ? Date.parse(raw) : 0

    return Number.isFinite(timestamp) ? timestamp : 0
}

const isSameSessionContext = (a, b) => {
    if (!a?.sessionId || !b?.sessionId) return false

    return String(a.officeId || '') === String(b.officeId || '') &&
        String(a.sessionId || '').toUpperCase() === String(b.sessionId || '').toUpperCase()
}

const applyMainItems = (state, items) => {
    const mainCartItems = normalizeItems(items)

    return {
        ...state,
        mainCartItems,
        mainCartReady: true,
        cartItems: getVisibleCartItems({
            ...state,
            mainCartItems,
        }),
    }
}

const applySessionItems = (state, payload = {}, message = '', options = {}) => {
    const sessionCartContext = getSessionContextFromPayload(payload, state.sessionCartContext)
    const incomingUpdatedAt = getSessionUpdatedAt(payload)
    const sameContext = isSameSessionContext(state.sessionCartContext, sessionCartContext)
    const isStale = sameContext &&
        incomingUpdatedAt > 0 &&
        state.sessionCartUpdatedAt > 0 &&
        incomingUpdatedAt < state.sessionCartUpdatedAt

    if (isStale) {
        return {
            ...state,
            updatingCart: options.keepUpdating === true ? true : false,
            loading: false,
        }
    }

    const sessionCartItems = normalizeItems(getSessionItemsFromPayload(payload))
    const activeCartScope = sessionCartContext?.sessionId ? CART_SCOPE_SESSION : state.activeCartScope
    const nextState = {
        ...state,
        sessionCartItems,
        sessionCartContext,
        sessionCartReady: true,
        sessionCartUpdatedAt: incomingUpdatedAt || state.sessionCartUpdatedAt,
        activeCartScope,
        updatingCart: options.keepUpdating === true,
        loading: false,
        textAlert: message || payload.message || state.textAlert,
        showAlert: !!(message || payload.message),
        variantAlert: 'success',
    }

    return {
        ...nextState,
        cartItems: getVisibleCartItems(nextState),
    }
}

export default (state = initState, action) => {
    const mes = action.payload?.message === undefined ? state.textAlert : action.payload?.message

    switch (action.type) {
        case 'SET_CART_ACTIVE_SCOPE': {
            const requestedScope = action.payload === CART_SCOPE_SESSION ? CART_SCOPE_SESSION : CART_SCOPE_GLOBAL
            const activeCartScope = requestedScope === CART_SCOPE_SESSION && state.sessionCartContext?.sessionId
                ? CART_SCOPE_SESSION
                : CART_SCOPE_GLOBAL
            const nextState = {
                ...state,
                activeCartScope,
            }

            return {
                ...nextState,
                cartItems: getVisibleCartItems(nextState),
            }
        }

        case 'HYDRATE_CART_FROM_SALES_SESSION':
            return applySessionItems(state, action.payload || {}, '')

        case 'SESSION_CART_LOCAL_SET':
            return applySessionItems(state, action.payload || {}, mes || 'Корзина сессии обновлена')

        case 'OFFICE_SALES_SESSION_CART_UPDATE_SUCCESS':
            return applySessionItems(state, action.payload || {}, mes || 'Корзина сессии сохранена')

        case 'OFFICE_SALES_SESSION_CART_UPDATE_FAILURE':
        case SESSION_CART_MUTATION_FAILURE:
            return {
                ...state,
                updatingCart: false,
                error: action.payload?.error || action.payload?.message || 'Ошибка сохранения корзины сессии',
                textAlert: action.payload?.error || action.payload?.message || 'Ошибка сохранения корзины сессии',
                variantAlert: 'warning',
                showAlert: true,
                loading: false,
            }

        case SESSION_CART_MUTATION_REQUEST:
            return {
                ...state,
                updatingCart: true,
                error: null,
            }

        case SESSION_CART_MUTATION_SUCCESS:
            return applySessionItems(
                state,
                action.payload || {},
                action.payload?.message || 'Корзина сессии обновлена',
                { keepUpdating: action.payload?.keepUpdating === true }
            )

        case 'CLEAR_CART_SESSION_SCOPE': {
            const nextState = {
                ...state,
                activeCartScope: CART_SCOPE_GLOBAL,
                sessionCartItems: [],
                sessionCartContext: null,
                sessionCartReady: false,
                sessionCartUpdatedAt: 0,
            }

            return {
                ...nextState,
                cartItems: getVisibleCartItems(nextState),
            }
        }

        case cartConstants.UPDATE_CART_ITEM_QUANTITY_REQUEST:
        case cartConstants.UPDATE_CART_ITEM_CONFIG_REQUEST: {
            const cartItemKey = getActionCartItemKey(action)

            return {
                ...state,
                updatingCart: cartItemKey ? state.updatingCart : true,
                updatingCartItems: setUpdatingCartItem(state.updatingCartItems, cartItemKey, true),
                error: null,
            }
        }

        case cartConstants.ADD_TO_CART_REQUEST:
        case cartConstants.REMOVE_CART_ITEM_REQUEST:
            return {
                ...state,
                updatingCart: true,
                error: null,
            }

        case cartConstants.GET_CART_ITEMS_REQUEST:
            return {
                ...state,
                updatingCart: action.payload?.silent ? state.updatingCart : true,
                error: null,
            }

        case cartConstants.UPDATE_CART_ITEM_QUANTITY_SUCCESS:
        case cartConstants.UPDATE_CART_ITEM_CONFIG_SUCCESS: {
            const cartItemKey = getActionCartItemKey(action)

            if (action.payload?.isSessionCart) {
                const nextState = applySessionItems(state, action.payload || {}, mes)

                return {
                    ...nextState,
                    updatingCart: false,
                    updatingCartItems: setUpdatingCartItem(state.updatingCartItems, cartItemKey, false),
                }
            }

            const nextState = applyMainItems(state, action.payload?.updatedCart || state.mainCartItems)

            return {
                ...nextState,
                updatingCart: false,
                updatingCartItems: setUpdatingCartItem(state.updatingCartItems, cartItemKey, false),
                textAlert: mes,
                showAlert: true,
                variantAlert: 'success',
                loading: false
            }
        }

        case cartConstants.UPDATE_CART_ITEM_QUANTITY_FAILURE:
        case cartConstants.UPDATE_CART_ITEM_CONFIG_FAILURE: {
            const cartItemKey = getActionCartItemKey(action)

            return {
                ...state,
                updatingCart: false,
                updatingCartItems: setUpdatingCartItem(state.updatingCartItems, cartItemKey, false),
                textAlert: mes,
                variantAlert: 'warning',
                showAlert: true,
                loading: false,
            }
        }

        case cartConstants.ADD_TO_CART_SUCCESS: {
            if (action.payload?.isSessionCart) {
                return applySessionItems(state, action.payload || {}, mes || 'Добавлено в корзину сессии')
            }

            const nextState = applyMainItems(state, action.payload?.cartItems || state.mainCartItems)

            return {
                ...nextState,
                updatingCart: false,
                textAlert: mes,
                showAlert: true,
                variantAlert: 'success',
                loading: false
            }
        }

        case cartConstants.ADD_TO_CART_FAILURE:
            return {
                ...state,
                updatingCart: false,
                textAlert: mes,
                variantAlert: 'warning',
                showAlert: true,
                loading: false,
            }

        case cartConstants.GET_CART_ITEMS_SUCCESS: {
            const nextState = applyMainItems(state, action.payload?.cartItems || [])

            return {
                ...nextState,
                mainCartReady: true,
                updatingCart: false,
            }
        }

        case cartConstants.GET_CART_ITEMS_FAILURE:
            return {
                ...state,
                mainCartReady: true,
                updatingCart: false,
                error: action.payload?.error || null
            }

        case cartConstants.UPDATE_TO_CART_SUCCESS: {
            const nextState = applyMainItems(state, action.payload?.cartItems || state.mainCartItems)

            return {
                ...nextState,
                textAlert: 'Корзина обновлена!',
                showAlert: true,
                updatingCart: false,
            }
        }

        case cartConstants.SHOW_MODAL_CART:
            return {
                ...state,
                showModal: !state.showModal
            }

        case cartConstants.SHOW_MODAL_CART_FALSE:
            return {
                ...state,
                showModal: false
            }

        case cartConstants.REMOVE_CART_ITEM_SUCCESS: {
            if (action.payload?.isSessionCart) {
                return applySessionItems(state, action.payload || {}, mes || 'Товар удалён из корзины сессии')
            }

            return {
                ...state,
                updatingCart: false,
                showAlert: true,
                textAlert: mes || 'Товар успешно удален с корзины!',
                variantAlert: 'success'
            }
        }

        case cartConstants.REMOVE_CART_ITEM_FAILURE:
            return {
                ...state,
                updatingCart: false,
                showAlert: true,
                textAlert: mes || 'Что-то пошло не так!',
                variantAlert: 'warning',
            }

        case cartConstants.SET_CLOSE_ALERT:
            return {
                ...state,
                showAlert: false,
            }

        case cartConstants.RESET_CART:
            return {
                ...initState
            }

        default:
            return state
    }
}

export const setCloseAlert = (payload) => ({
    type: cartConstants.SET_CLOSE_ALERT,
    payload
})

export const setShowModalCart = (payload) => ({
    type: cartConstants.SHOW_MODAL_CART,
    payload
})

export const setHideModalCart = (payload) => ({
    type: cartConstants.SHOW_MODAL_CART_FALSE,
    payload
})
